import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

import axios from "axios";

/**
 * ```js
 * const MyProtectedComponent = withAuthenticationRequired(MyComponent);
 * ```
 *
 * When you wrap your components in this Higher Order Component and an anonymous user visits your component
 * they will be redirected to the login page; after login they will be returned to the page they were redirected from.
 */

const defaultOnRedirecting = () => <></>;

/**
* @ignore
*/
const defaultOnBeforeAuthentication = async () => {/* noop */};

/**
 * @ignore
 */
const defaultReturnTo = () =>
  `${window.location.pathname}${window.location.search}`;

const withAuthenticationRequired = (
  Component,
  options
) => {
  return function WithAuthenticationRequired(props) {
    const [token, setToken] = useState(null);

    const {
      returnTo = defaultReturnTo,
      onRedirecting = defaultOnRedirecting,
      onBeforeAuthentication = defaultOnBeforeAuthentication,
      loginOptions
    } = options;

    const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } =
      useAuth0();

    const setUserToken = async () => {
      try {
          const token = await getAccessTokenSilently({authorizationParams: {
              audience: "https://app.deepidentify.ai/api"
          }});
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          setToken(token);
      } catch (e) {
          console.error(e);
      }
    }

    useEffect(() => {
      if (isLoading || isAuthenticated) {
        return;
      }

      const opts = {
        ...loginOptions,
        appState: {
          ...(loginOptions && loginOptions.appState),
          returnTo: typeof returnTo === 'function' ? returnTo() : returnTo,
        },
      };
      (async () => {
        await onBeforeAuthentication();
        await loginWithRedirect(opts);
      })();
    }, [
      isLoading,
      isAuthenticated,
      loginWithRedirect,
      onBeforeAuthentication,
      loginOptions,
      returnTo,
    ]);

    useEffect(() => {
      if (isAuthenticated && !token) {
        setUserToken();
      }
    }, [isAuthenticated, token])

    return token ? <Component {...props} /> : onRedirecting();
  };
};

export default withAuthenticationRequired;